// client/src/hooks/core/usePaginatedCollection.ts
import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  collection as fbCollection,
  query,
  where,
  orderBy,
  limit as fbLimit,
  startAfter,
  getDocs,
  QueryConstraint,
  DocumentSnapshot,
} from 'firebase/firestore';
import { db } from '../../config/firebase';

export interface UsePaginatedCollectionOptions {
  /** Firestore collection path (e.g. 'contacts' or 'organizations/{orgId}/employees') */
  collection: string;
  /** Organization ID — used for `where('organizationId', '==', orgId)` when hasOrgField=true */
  orgId: string;
  /** Records per page (default 25) */
  pageSize?: number;
  /** Order field (default 'updatedAt') */
  orderField?: string;
  /** Order direction (default 'desc') */
  orderDir?: 'asc' | 'desc';
  /** Additional Firestore where/constraint clauses beyond organizationId */
  whereClauses?: QueryConstraint[];
  /** React Query key (required; use a query-key factory) */
  queryKey: readonly unknown[];
  /** staleTime override (default 5 min) */
  staleTime?: number;
  /** Enable toggle (default !!orgId) */
  enabled?: boolean;
  /** Set to false to skip the automatic `where('organizationId', '==', orgId)` clause
   *  (for collections without an org field, e.g. legacy AppSheet mirrors). Default true. */
  hasOrgField?: boolean;
  /** Additional field name for the document ID (e.g. 'leadId', 'contactId').
   *  When set, `doc.id` is written under this key in addition to 'id'. */
  idField?: string;
}

export interface PaginatedPage<T> {
  items: T[];
  lastDoc: DocumentSnapshot | null;
  hasMore: boolean;
}

export interface UsePaginatedCollectionResult<T> {
  items: T[];
  fetchNextPage: () => Promise<unknown>;
  hasNextPage: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  error: Error | null;
  totalFetched: number;
  refetch: () => Promise<unknown>;
}

/**
 * Cursor-paginated Firestore list hook.
 *
 * Wraps `useInfiniteQuery` with Firestore `startAfter` cursor semantics. Modeled on
 * `useCallLogs`. Search filtering is NOT handled here — compose with `<PageSearch>`
 * (Typesense-backed) instead of passing search text through the query key.
 *
 * @example
 * ```tsx
 * const { items, fetchNextPage, hasNextPage, isLoading } = usePaginatedCollection<Contact>({
 *   collection: 'contacts',
 *   orgId: currentOrganization.organizationId,
 *   pageSize: 25,
 *   orderField: 'fullName',
 *   orderDir: 'asc',
 *   queryKey: contactKeys.paginated(filters),
 *   whereClauses: buildContactFilters(filters).whereClauses,
 * });
 * ```
 */
export function usePaginatedCollection<T>(
  opts: UsePaginatedCollectionOptions
): UsePaginatedCollectionResult<T> {
  const {
    collection,
    orgId,
    pageSize = 25,
    orderField = 'updatedAt',
    orderDir = 'desc',
    whereClauses = [],
    queryKey,
    staleTime = 5 * 60 * 1000,
    enabled,
    hasOrgField = true,
    idField,
  } = opts;

  const effectiveEnabled = enabled ?? !!orgId;

  const infinite = useInfiniteQuery<PaginatedPage<T>>({
    queryKey,
    queryFn: async ({ pageParam }) => {
      const constraints: QueryConstraint[] = [];
      if (hasOrgField && orgId) {
        constraints.push(where('organizationId', '==', orgId));
      }
      for (const c of whereClauses) constraints.push(c);
      constraints.push(orderBy(orderField, orderDir));
      if (pageParam) {
        constraints.push(startAfter(pageParam as DocumentSnapshot));
      }
      constraints.push(fbLimit(pageSize));

      const snap = await getDocs(query(fbCollection(db, collection), ...constraints));

      const items = snap.docs.map((d) => ({
        ...d.data(),
        id: d.id,
        ...(idField ? { [idField]: d.id } : {}),
      } as unknown as T));
      const lastDoc = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null;
      const hasMore = snap.docs.length === pageSize;

      return { items, lastDoc, hasMore };
    },
    initialPageParam: null as DocumentSnapshot | null,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.lastDoc : undefined),
    enabled: effectiveEnabled,
    staleTime,
  });

  const items = useMemo<T[]>(
    () => infinite.data?.pages.flatMap((p) => p.items) ?? [],
    [infinite.data]
  );

  return {
    items,
    fetchNextPage: infinite.fetchNextPage,
    hasNextPage: !!infinite.hasNextPage,
    isLoading: infinite.isLoading,
    isFetching: infinite.isFetching,
    isFetchingNextPage: infinite.isFetchingNextPage,
    error: (infinite.error as Error) ?? null,
    totalFetched: items.length,
    refetch: infinite.refetch,
  };
}
